/**
 * Столбцы ряда строки «Голоса клиента» (#2394 §9 п. 7): доля K/N корзины.
 * Пустая корзина — черта по низу и «нет данных», а не нулевой столбец;
 * нулевая доля при разговорах — тонкий столбец. Корзина, с которой сменилась
 * версия кодов, отделена пунктиром. Столбец — кнопка выбора корзины.
 */
import { useTranslation } from 'react-i18next';
import type { VoiceTrendBucket } from '@aica/api-types/schemas';
import { cn, formatNumber } from '@/lib/utils';
import {
  bucketShareText,
  seriesShares,
  type VoiceRowSeries,
  type VoiceSeriesUnit,
} from './voice-series-view-model';

/** Столбец не исчезает: настоящий ноль виден тонкой чертой. */
const MIN_BAR_PERCENT = 2;

interface VoiceTrendBarsProps {
  series: VoiceRowSeries;
  unit: VoiceSeriesUnit;
  selected: number | null;
  onSelect: (index: number) => void;
  /** Корзина словами (`bucketPeriodText`). */
  period: (bucket: VoiceTrendBucket, unit: VoiceSeriesUnit) => string;
}

export function VoiceTrendBars({
  series,
  unit,
  selected,
  onSelect,
  period,
}: VoiceTrendBarsProps) {
  const { t } = useTranslation('dashboard');
  const shares = seriesShares(series);
  const top = Math.max(1, ...shares.map((share) => share ?? 0));
  const { buckets } = series;
  if (buckets.length === 0) return null;
  return (
    <div>
      <div className="flex h-20 items-end gap-px" role="list">
        {buckets.map((bucket, index) => {
          const share = shares[index] ?? null;
          const count = series.counts[index] ?? null;
          // A covered bucket without analysed conversations (a weekend) is a
          // gap like an uncovered one — the share has no N — but not «no data».
          const label =
            bucket.covered && bucket.calls === 0
              ? t('voicePage.series.barNoCalls', { period: period(bucket, unit) })
              : share === null || count === null || bucket.calls === null
                ? t('voicePage.series.barEmpty', { period: period(bucket, unit) })
                : t('voicePage.series.bar', {
                    period: period(bucket, unit),
                    count,
                    total: formatNumber(bucket.calls),
                    share: bucketShareText(count, bucket.calls),
                  });
          const edge = series.breaks.includes(index)
            ? 'border-l border-dashed border-border'
            : undefined;
          if (share === null) {
            return (
              <div
                key={bucket.start}
                role="listitem"
                aria-label={label}
                title={label}
                className={cn('flex h-full flex-1 items-end', edge)}
                data-testid="voice-trend-empty"
              >
                <span className="h-px w-full bg-border" />
              </div>
            );
          }
          const height = Math.min(100, Math.max(MIN_BAR_PERCENT, (share / top) * 100));
          return (
            <div
              key={bucket.start}
              role="listitem"
              className={cn('flex h-full flex-1', edge)}
            >
              <button
                type="button"
                title={label}
                aria-label={label}
                aria-pressed={selected === index}
                onClick={() => onSelect(index)}
                className="group flex h-full w-full items-end justify-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                data-testid="voice-trend-bar"
              >
                <span
                  className={cn(
                    'w-full max-w-8 rounded-sm transition-colors',
                    selected === index
                      ? 'bg-primary'
                      : 'bg-primary/60 group-hover:bg-primary'
                  )}
                  style={{ height: `${height}%` }}
                />
              </button>
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-xs tabular-nums text-fg-muted">
        <span>{period(buckets[0]!, unit)}</span>
        {buckets.length > 1 && <span>{period(buckets[buckets.length - 1]!, unit)}</span>}
      </div>
    </div>
  );
}
